import React, {
  useEffect,
  useCallback,
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
} from 'react';

import cn from 'classnames';

import { useOutsideClick } from '@hooks';

import { Avatar } from '../Avatar';
import { IconButton } from '../Button';
import ButtonBase from '../Button/ButtonBase';
import Icon, { IconNamesType } from '../Icon';

interface IMenuDropdownContext {
  setOpen: Dispatch<SetStateAction<boolean>>;
}
const MenuDropdownContext = createContext<IMenuDropdownContext>({
  setOpen: () => {},
});

export const MenuDropdownItem = ({
  tabIndex,
  label,
  leftIcon,
  rightIcon,
  onClick,
  className,
  disabled = false,
}: {
  tabIndex?: number;
  label: string;
  leftIcon?: IconNamesType;
  rightIcon?: IconNamesType;
  className?: string;
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
  disabled?: boolean;
}): JSX.Element => {
  const { setOpen } = useContext(MenuDropdownContext);
  const handleClick = (e) => {
    if (disabled) return;
    setOpen(false);
    onClick(e);
  };

  return (
    <a
      tabIndex={disabled ? -1 : tabIndex}
      role="option"
      aria-disabled={disabled}
      key={label}
      onClick={handleClick}
      className={cn(
        `sp-dropdown-menu-item text-sp-neutral`,
        { 'text-sp-neutral-200 pointer-events-none': disabled },
        className,
      )}
    >
      {leftIcon && <Icon name={leftIcon} />}
      {label}
      {rightIcon && <Icon name={rightIcon} />}
    </a>
  );
};

export const MenuDropdown = ({
  text,
  iconName,
  children,
  iconOnly,
  avatarInitials,
}: {
  text?: string;
  iconName: IconNamesType;
  children: React.ReactNode;
  iconOnly?: boolean;
  avatarInitials?: string;
}): JSX.Element => {
  const { ref, open, setOpen } = useOutsideClick();

  const escape = useCallback((event) => {
    if (event.keyCode === 27) {
      setOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('keydown', escape);
    };
  }, [escape]);

  return (
    <span ref={ref}>
      {!iconOnly && !avatarInitials && (
        <ButtonBase
          onClick={() => setOpen(!open)}
          rightIcon={<Icon name={iconName} />}
          aria-haspopup="listbox"
          aria-label="menudropdown-btn"
          className="w-full justify-between p-4"
        >
          {text && (
            <span className="text-sp-neutral-800 font-semibold">{text}</span>
          )}
        </ButtonBase>
      )}
      {iconOnly && (
        <IconButton.Ghost iconName={iconName} onClick={() => setOpen(!open)} />
      )}
      {avatarInitials && (
        <ButtonBase
          onClick={() => setOpen(!open)}
          aria-haspopup="listbox"
          aria-label="menudropdown-btn"
          className="focus:ring-sp-neutral-100 rounded-full p-0 focus:ring-4"
        >
          <Avatar size="md" initials={avatarInitials} />
        </ButtonBase>
      )}

      <span className="relative -ml-px block">
        {open && (
          <div
            className={`border-sp-neutral-100 absolute right-0 z-50 mt-2 max-h-72 w-full min-w-max overflow-y-auto rounded-lg border bg-white shadow-lg`}
            role="listbox"
          >
            <MenuDropdownContext.Provider value={{ setOpen }}>
              {children}
            </MenuDropdownContext.Provider>
          </div>
        )}
      </span>
    </span>
  );
};

MenuDropdown.Item = MenuDropdownItem;

MenuDropdown.defaultProps = {
  iconName: 'chevronDown',
  iconOnly: false,
  avatarPlaceholder: null,
};
